// Random tables: fast dice, plus persistent one-click tables stored as custom
// library entries (one result per line; "3. text" or "2–4 text" ranges weight).
import { useState } from "react";
import { useStore } from "../state/store";
import { EntryEditor, ReferenceLibrary, useCustomEntries, type LibraryEntry } from "./ReferenceLibrary";

const DICE = [4, 6, 8, 10, 12, 20, 100];
const rollDie = (sides: number) => 1 + Math.floor(Math.random() * sides);

interface Outcome { min: number; max: number; text: string }
function parseTable(rows: string[]): { sides: number; outcomes: Outcome[] } {
  const outcomes: Outcome[] = [];
  let numbered = false;
  for (const row of rows) {
    const m = row.match(/^\s*(\d+)\s*(?:[-–—]\s*(\d+))?\s*[.:)]?\s+(.*)$/);
    if (m) { numbered = true; outcomes.push({ min: Number(m[1]), max: Number(m[2] || m[1]), text: m[3] }); }
    else outcomes.push({ min: 0, max: 0, text: row });
  }
  if (!numbered) return { sides: rows.length, outcomes: rows.map((text, i) => ({ min: i + 1, max: i + 1, text })) };
  const sides = Math.max(...outcomes.map((o) => o.max));
  return { sides, outcomes };
}

export function RandomTables() {
  const { logActivity } = useStore();
  const { entries, save } = useCustomEntries("Random tables");
  const [last, setLast] = useState<{ label: string; result: string } | null>(null);
  const [editing, setEditing] = useState<LibraryEntry | "new" | null>(null);

  const rollTable = (e: LibraryEntry) => {
    const { sides, outcomes } = parseTable(e.rows || []);
    if (!sides) return;
    const n = rollDie(sides);
    const hit = outcomes.find((o) => n >= o.min && n <= o.max);
    const result = `d${sides} → ${n}: ${hit?.text || "(no entry for that number)"}`;
    setLast({ label: e.title, result });
    logActivity({ text: `${e.title}: ${hit?.text || n}`, meta: `rolled ${n} on d${sides}` });
  };

  return (
    <ReferenceLibrary page="Random tables" intro="Roll common dice or build persistent one-click tables for live play.">
      <div className="fast-roll">
        <span className="eyebrow">Fast roll</span>
        {DICE.map((d) => <button key={d} onClick={() => { const n = rollDie(d); setLast({ label: `d${d}`, result: String(n) }); }}>d{d}</button>)}
        {last && <span className="roll-result"><strong>{last.label}</strong> {last.result}</span>}
      </div>
      <div className="table-library">
        <span className="eyebrow">Live roller</span>
        <p className="muted small">Add a table with one possible result per line. Plain lines roll a d(N); numbered lines like "3. text" or "2–4 text" set weighted ranges.</p>
        {editing && <EntryEditor initial={editing === "new" ? undefined : editing} onCancel={() => setEditing(null)} onSave={(e) => { save(editing === "new" ? [e, ...entries] : entries.map((x) => (x.id === e.id ? e : x))); setEditing(null); }} />}
        <div className="roller-list">
          {entries.map((e) => { const t = parseTable(e.rows || []); return (
            <div key={e.id} className="roller"><strong>{e.title}</strong><span className="muted small">d{t.sides} · {t.outcomes.length} results</span>
              <span className="spacer" /><button className="primary small" onClick={() => rollTable(e)}>Roll</button><button className="ghost small" onClick={() => setEditing(e)}>edit</button></div>
          ); })}
          {!entries.length && <p className="empty">No tables yet. Use Add entry above and put one result per line.</p>}
        </div>
      </div>
    </ReferenceLibrary>
  );
}
